export interface ColorPalette {
  id: string;
  name: string;
  description: string;
  colors: string[];
  defaultColorIndex: number;
  isCustom?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export const COLOR_PALETTES: Record<string, ColorPalette> = {
  fireforge: {
    id: 'fireforge',
    name: 'FireForge',
    description: 'Bold oranges and reds with complementary colors',
    colors: [
      '#FF4500', // Primary Flame
      '#f97316', // Molten Orange  
      '#dc2626', // Tertiary Crimson
      '#ea580c', // Orange-600
      '#b91c1c', // Red-700
      '#fed7aa', // Orange-200
      '#fecaca', // Red-200
      '#fdba74', // Orange-300
    ],
    defaultColorIndex: 0,
  },
  business: {
    id: 'business',
    name: 'Business',
    description: 'Professional blues and grays for corporate presentations',
    colors: [
      '#1e40af', // Blue-800
      '#3b82f6', // Blue-500
      '#60a5fa', // Blue-400
      '#93c5fd', // Blue-300
      '#374151', // Gray-700
      '#6b7280', // Gray-500
      '#9ca3af', // Gray-400
      '#d1d5db', // Gray-300
    ],
    defaultColorIndex: 0,
  },
  vibrant: {
    id: 'vibrant',
    name: 'Vibrant',
    description: 'Bright, energetic colors for dynamic visualizations',
    colors: [
      '#ec4899', // Pink-500
      '#8b5cf6', // Violet-500
      '#06b6d4', // Cyan-500
      '#10b981', // Emerald-500
      '#f59e0b', // Amber-500
      '#ef4444', // Red-500
      '#84cc16', // Lime-500
      '#6366f1', // Indigo-500
    ],
    defaultColorIndex: 0,
  },
  pastel: {
    id: 'pastel',
    name: 'Pastel',
    description: 'Soft, muted tones for gentle, approachable charts',
    colors: [
      '#fbb6ce', // Pink-300
      '#c4b5fd', // Violet-300
      '#67e8f9', // Cyan-300
      '#6ee7b7', // Emerald-300
      '#fcd34d', // Amber-300
      '#fca5a5', // Red-300
      '#bef264', // Lime-300
      '#a5b4fc', // Indigo-300
    ],
    defaultColorIndex: 0,
  },
  monochrome: {
    id: 'monochrome',
    name: 'Monochrome',
    description: 'Elegant grayscale palette for minimalist designs',
    colors: [
      '#111827', // Gray-900
      '#374151', // Gray-700
      '#6b7280', // Gray-500
      '#9ca3af', // Gray-400
      '#d1d5db', // Gray-300
      '#e5e7eb', // Gray-200
      '#f3f4f6', // Gray-100
      '#f9fafb', // Gray-50
    ],
    defaultColorIndex: 0,
  },
};

export const getColorPalette = (paletteId: string, customPalettes: ColorPalette[] = []): string[] => {
  // First check custom palettes
  const customPalette = customPalettes.find(p => p.id === paletteId);
  if (customPalette) {
    return customPalette.colors;
  }
  
  // Then check built-in palettes
  const builtInPalette = COLOR_PALETTES[paletteId];
  return builtInPalette ? builtInPalette.colors : COLOR_PALETTES.fireforge.colors;
};

export const getAllColorPalettes = (customPalettes: ColorPalette[] = []): ColorPalette[] => {
  const builtInPalettes = Object.values(COLOR_PALETTES);
  const customPalettesWithFlag = customPalettes.map(palette => ({
    ...palette,
    isCustom: true,
  }));
  return [...builtInPalettes, ...customPalettesWithFlag];
};

export const getBuiltInColorPalettes = (): ColorPalette[] => {
  return Object.values(COLOR_PALETTES);
};

export const createCustomPalette = (
  name: string,
  colors: string[],
  description?: string
): Omit<ColorPalette, 'id'> => {
  return {
    name,
    description: description || `Custom palette with ${colors.length} colors`,
    colors,
    defaultColorIndex: 0,
    isCustom: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}; 